import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Button, Checkbox, FormLabel, getCheckboxCheckedValue, getHumanReadableTimeout, Input } from '@infologistics/frontend-libraries';

import { getErrorText, hasError } from '@utils/utils';
import { SECONDS_IN_MINUTE, UserField } from '@const/consts';
import { AuthTranslations, Warning } from '@const/translations';
import { ConfirmCodeState, ICodeInputProps as IProps } from './types';

import styles from '@views/auth/components/AuthNew.module.scss';
import { FormikErrors } from 'formik';
import { LoginButtons } from '../LoginButtons';

const CodeInput: FC<IProps> = (props) => {
  const {
    onChangeNumberClick,
    onNavigationClick,
    onEmailSubmit,
    onSSOLoginClick,
    onServerErrorChange,
    setFieldValue,
    handleChange,
    handleSubmit,
    setErrors,
    setTouched,
    errors,
    isSending,
    touched,
    values,
  } = props;

  const { t } = useTranslation(['auth', 'common']);
  const [codeTimeout, setCodeTimeout] = useState(SECONDS_IN_MINUTE);

  const isSms = !!onChangeNumberClick;
  const { email, password, phone, remember, code } = values;

  useEffect(() => {
    resetCode();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (!codeTimeout) return;

      setCodeTimeout((prevTimeout) => prevTimeout - 1);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [codeTimeout]);

  const resetCode = (): Promise<void | FormikErrors<ConfirmCodeState>> => setFieldValue('code', null);

  const handleSendCodeAgain = (): void => {
    if (isSms && onChangeNumberClick) onChangeNumberClick();
    else if (onEmailSubmit) {
      resetCode();
      onEmailSubmit(remember, { email, password });
      setCodeTimeout(60);
    }
  };

  const handleSubmitBtnClick = (): void => {
    if (!code) {
      setTouched({ [UserField.CODE]: true });
      setErrors({ [UserField.CODE]: Warning.required });
      return;
    }

    if (onEmailSubmit) {
      onEmailSubmit(remember, { email, password }, code);
      return;
    }

    handleSubmit();
  };

  const handleLoginBySSOClick = (): void => {
    onSSOLoginClick();
    onServerErrorChange(undefined);
  };

  return (
    <>
      <div className='mb-4'>
        <div className='font-md mb-4'>
          <p className='mb-0'>{isSms ? t('auth:phoneVerify.codeSent') : t('auth:emailCodeSend', { email })}</p>
          {phone && (
            <p className='d-flex'>
              <span className='mr-2'>{values?.phone}</span>
              <Button onClick={onChangeNumberClick} isDisabled={isSending} theme='link' linkStyle='none'>
                {t('auth:phoneVerify.change')}
              </Button>
            </p>
          )}
        </div>
        <div className='mb-6'>
          <FormLabel htmlFor={UserField.CODE} text={t('auth:phoneVerify.enterCode')} className={cn(styles.label, 'text-muted')} />
          <Input
            id={UserField.CODE}
            name={UserField.CODE}
            onChange={handleChange}
            value={values[UserField.CODE] ?? ''}
            hasError={hasError(UserField.CODE, errors, touched)}
            errorText={t(getErrorText(UserField.CODE, errors))}
            rightIcon='IconLock'
            rightIconProps={{ withoutFill: true, size: 'sm', classes: styles.input_icon }}
            inputSize='md'
            classes={{ wrapper: cn(styles.input), input: cn(styles.inputs, styles.input_radius) }}
          />
        </div>
        {codeTimeout !== 0 && (
          <p className='text-muted'>
            {t('auth:phoneVerify.newCodeTimeout')} {getHumanReadableTimeout(codeTimeout)}
          </p>
        )}
        {!codeTimeout && (
          <Button onClick={handleSendCodeAgain} isDisabled={isSending} theme='link' linkStyle='none'>
            {t('auth:phoneVerify.newCode')}
          </Button>
        )}
      </div>

      {isSms && (
        <FormLabel text={t(AuthTranslations.remember)} className={cn(styles.label, 'd-inline-flex flex-row-reverse align-items-center mb-4')}>
          <Checkbox
            name='remember'
            checked={getCheckboxCheckedValue(values.remember)}
            onChange={handleChange}
            className='mr-2 text-nowrap'
            size='large'
          />
        </FormLabel>
      )}

      <LoginButtons
        isDisabled={isSending}
        main={{
          onClick: handleSubmitBtnClick,
          text: AuthTranslations.signIn,
        }}
        left={{
          onClick: onNavigationClick,
          text: AuthTranslations.loginByEmail,
        }}
        right={{
          onClick: handleLoginBySSOClick,
          text: AuthTranslations.loginBySSO,
        }}
      />
    </>
  );
};

export default CodeInput;
